import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import { tomApi, processingApi, organizationApi, openPdfExport } from '../services/api';
import {
  TomMeasure,
  Organization,
  TomCategory,
  TomStatus,
  TomScope,
  TomEffectiveness,
  ProcessingActivity,
  ProcessingStatus,
  RiskLevel
} from '@dsgvo-buddy/types';
import {
  Title,
  Text,
  Button,
  FlexBox,
  Card,
  CardHeader,
  TabContainer,
  Tab,
  MessageBox,
  ObjectStatus,
  Icon,
  BusyIndicator,
  MessageStrip,
  Table,
  TableColumn,
  TableRow,
  TableCell
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';

const categoryConfig: Record<TomCategory, { label: string }> = {
  [TomCategory.ACCESS_CONTROL]: { label: 'Zugriffskontrolle' },
  [TomCategory.TRANSFER_CONTROL]: { label: 'Weitergabekontrolle' },
  [TomCategory.INPUT_CONTROL]: { label: 'Eingabekontrolle' },
  [TomCategory.AVAILABILITY]: { label: 'Verfügbarkeit' },
  [TomCategory.RESILIENCE]: { label: 'Belastbarkeit' },
  [TomCategory.RECOVERY]: { label: 'Wiederherstellung' },
  [TomCategory.SEPARATION]: { label: 'Trennbarkeit' },
  [TomCategory.DELETION]: { label: 'Löschung' },
  [TomCategory.PRIVACY_BY_DESIGN]: { label: 'Privacy by Design' },
  [TomCategory.ENCRYPTION]: { label: 'Verschlüsselung' },
  [TomCategory.PSEUDONYMIZATION]: { label: 'Pseudonymisierung' }
};

const statusConfig: Record<TomStatus, { label: string; state: string }> = {
  [TomStatus.PLANNED]: { label: 'Geplant', state: 'Information' },
  [TomStatus.IMPLEMENTED]: { label: 'Umgesetzt', state: 'Success' },
  [TomStatus.OUTDATED]: { label: 'Veraltet', state: 'Error' },
  [TomStatus.COMPENSATING]: { label: 'Kompensierend', state: 'Warning' }
};

const effectivenessConfig: Record<TomEffectiveness, { label: string; state: string }> = {
  [TomEffectiveness.HIGH]: { label: 'Hoch', state: 'Success' },
  [TomEffectiveness.MEDIUM]: { label: 'Mittel', state: 'Warning' },
  [TomEffectiveness.LOW]: { label: 'Niedrig', state: 'Error' },
  [TomEffectiveness.NOT_ASSESSED]: { label: 'Nicht bewertet', state: 'None' }
};

const scopeLabels: Record<TomScope, string> = {
  [TomScope.GLOBAL]: 'Global (organisationsweit)',
  [TomScope.PER_PROCESSING]: 'Pro Verarbeitung (individuell)'
};

const processingStatusConfig: Record<ProcessingStatus, { label: string; state: string }> = {
  [ProcessingStatus.DRAFT]: { label: 'Entwurf', state: 'None' },
  [ProcessingStatus.IN_REVIEW]: { label: 'In Prüfung', state: 'Information' },
  [ProcessingStatus.APPROVED]: { label: 'Freigegeben', state: 'Success' },
  [ProcessingStatus.ARCHIVED]: { label: 'Archiviert', state: 'None' }
};

const riskConfig: Record<RiskLevel, { label: string; state: string }> = {
  [RiskLevel.LOW]: { label: 'Niedrig', state: 'Success' },
  [RiskLevel.MEDIUM]: { label: 'Mittel', state: 'Warning' },
  [RiskLevel.HIGH]: { label: 'Hoch', state: 'Error' }
};

const InfoRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div style={{ display: 'flex', padding: '0.5rem 0', borderBottom: '1px solid var(--sapGroup_ContentBorderColor, #e5e5e5)' }}>
    <Text style={{ fontWeight: '600', color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem', width: '180px', flexShrink: 0 }}>{label}</Text>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

export default function TomDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { canEdit, canDelete } = useUserRole();

  const [measure, setMeasure] = useState<TomMeasure | null>(null);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [linkedActivities, setLinkedActivities] = useState<ProcessingActivity[]>([]);
  const [allActivities, setAllActivities] = useState<ProcessingActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    setLoading(true);
    const orgsResponse = await organizationApi.getAll();
    if (orgsResponse.success && orgsResponse.data && orgsResponse.data.length > 0) {
      const org = orgsResponse.data[0];
      setCurrentOrg(org);
      if (id) {
        const [measureRes, linkedRes, allRes] = await Promise.all([
          tomApi.getById(parseInt(id), org.id),
          tomApi.getLinkedActivities(parseInt(id), org.id),
          processingApi.getByOrganization(org.id)
        ]);
        if (measureRes.success && measureRes.data) {
          setMeasure(measureRes.data);
        } else {
          navigate('/tom');
        }
        if (linkedRes.success && linkedRes.data) setLinkedActivities(linkedRes.data);
        if (allRes.success && allRes.data) setAllActivities(allRes.data);
      }
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!measure || !currentOrg) return;
    const response = await tomApi.delete(measure.id, currentOrg.id);
    if (response.success) {
      navigate('/tom');
    }
  };

  const handleLink = async (activityId: number) => {
    if (!measure || !currentOrg) return;
    await tomApi.linkActivity(measure.id, activityId, currentOrg.id);
    loadData();
    setShowLinkDialog(false);
  };

  const handleUnlink = async (activityId: number) => {
    if (!measure || !currentOrg) return;
    await tomApi.unlinkActivity(measure.id, activityId, currentOrg.id);
    loadData();
  };

  const handleStatusChange = async (newStatus: TomStatus) => {
    if (!measure || !currentOrg) return;
    const response = await tomApi.update(measure.id, currentOrg.id, { status: newStatus });
    if (response.success && response.data) {
      setMeasure(response.data);
    }
  };

  if (loading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  if (!measure) return null;

  const linkedIds = new Set(linkedActivities.map(a => a.id));
  const unlinkableActivities = allActivities.filter(a => !linkedIds.has(a.id));

  // Review date warning
  const reviewWarning = measure.review_date && new Date(measure.review_date) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

  return (
    <div className="page-layout--detail">
      {/* Header */}
      <FlexBox justifyContent="SpaceBetween" alignItems="Start" style={{ marginBottom: '1.5rem' }}>
        <div>
          <FlexBox alignItems="Center" style={{ gap: '0.75rem', marginBottom: '0.25rem' }}>
            <Button icon="nav-back" design="Transparent" onClick={() => navigate('/tom')} tooltip="Zurück zur Liste" />
            <Title level="H3">{measure.name}</Title>
            <ObjectStatus state={statusConfig[measure.status]?.state as any} showDefaultIcon>
              {statusConfig[measure.status]?.label}
            </ObjectStatus>
          </FlexBox>
          <Text style={{ color: 'var(--sapNeutralTextColor)', marginLeft: '2.75rem' }}>
            {categoryConfig[measure.category]?.label} — {scopeLabels[measure.scope]}
          </Text>
        </div>
        <FlexBox style={{ gap: '0.5rem' }}>
          <Button icon="pdf-attachment" design="Default" onClick={() => currentOrg && openPdfExport('tom/' + id, currentOrg.id)}>PDF</Button>
          {canEdit && (
            <Button icon="edit" design="Default" onClick={() => navigate(`/tom/${id}/edit`)}>
              Bearbeiten
            </Button>
          )}
          {canDelete && (
            <Button icon="delete" design="Negative" onClick={() => setShowDeleteDialog(true)}>
              Löschen
            </Button>
          )}
        </FlexBox>
      </FlexBox>

      {reviewWarning && (
        <MessageStrip design="Warning" style={{ marginBottom: '1rem' }}>
          Prüftermin fällig: {new Date(measure.review_date!).toLocaleDateString('de-DE')}
        </MessageStrip>
      )}

      {/* Tabs */}
      <TabContainer onTabSelect={() => {}} style={{ marginBottom: '1.5rem' }}>
        <Tab text="Überblick" icon="hint" selected>
          <div style={{ padding: '1rem 0' }}>
            <FlexBox wrap="Wrap" style={{ gap: '1rem' }}>
              {/* Basic Info */}
              <Card style={{ flex: '1 1 500px' }} header={<CardHeader titleText="Allgemeine Informationen" avatar={<Icon name="shield" />} />}>
                <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                  <InfoRow label="Name"><Text>{measure.name}</Text></InfoRow>
                  <InfoRow label="Beschreibung"><Text>{measure.description || '-'}</Text></InfoRow>
                  <InfoRow label="Kategorie"><Text>{categoryConfig[measure.category]?.label}</Text></InfoRow>
                  <InfoRow label="Geltungsbereich"><Text>{scopeLabels[measure.scope]}</Text></InfoRow>
                  <InfoRow label="Verantwortlich"><Text>{measure.responsible_person || '-'}</Text></InfoRow>
                  <InfoRow label="Erstellt am"><Text>{new Date(measure.created_at).toLocaleDateString('de-DE')}</Text></InfoRow>
                  <InfoRow label="Aktualisiert"><Text>{new Date(measure.updated_at).toLocaleDateString('de-DE')}</Text></InfoRow>
                </div>
              </Card>

              {/* Assessment */}
              <Card style={{ flex: '1 1 300px' }} header={<CardHeader titleText="Bewertung" avatar={<Icon name="inspection" />} />}>
                <div style={{ padding: '0.5rem 1.25rem 1.25rem' }}>
                  <InfoRow label="Status">
                    <ObjectStatus state={statusConfig[measure.status]?.state as any} showDefaultIcon>
                      {statusConfig[measure.status]?.label}
                    </ObjectStatus>
                  </InfoRow>
                  <InfoRow label="Wirksamkeit">
                    <ObjectStatus state={effectivenessConfig[measure.effectiveness]?.state as any}>
                      {effectivenessConfig[measure.effectiveness]?.label}
                    </ObjectStatus>
                  </InfoRow>
                  <InfoRow label="Prüftermin">
                    <Text>{measure.review_date ? new Date(measure.review_date).toLocaleDateString('de-DE') : '-'}</Text>
                  </InfoRow>
                  <InfoRow label="Nachweis"><Text>{measure.evidence_reference || '-'}</Text></InfoRow>
                  <InfoRow label="Notizen"><Text>{measure.notes || '-'}</Text></InfoRow>

                  {/* Quick Status Actions */}
                  {canEdit && (
                    <div style={{ marginTop: '1rem' }}>
                      <Text style={{ fontWeight: '600', fontSize: '0.8125rem', display: 'block', marginBottom: '0.5rem' }}>Status ändern:</Text>
                      <FlexBox wrap="Wrap" style={{ gap: '0.5rem' }}>
                        {Object.entries(statusConfig)
                          .filter(([key]) => key !== measure.status)
                          .map(([key, cfg]) => (
                            <Button
                              key={key}
                              design="Default"
                              onClick={() => handleStatusChange(key as TomStatus)}
                              style={{ fontSize: '0.8125rem' }}
                            >
                              {cfg.label}
                            </Button>
                          ))}
                      </FlexBox>
                    </div>
                  )}
                </div>
              </Card>
            </FlexBox>
          </div>
        </Tab>

        <Tab text={`Verarbeitungen (${linkedActivities.length})`} icon="documents">
          <div style={{ padding: '1rem 0' }}>
            <Card
              header={
                <CardHeader
                  titleText={`${linkedActivities.length} verknüpfte Verarbeitungen`}
                  avatar={<Icon name="documents" />}
                  action={
                    <Button icon="add" design="Transparent" onClick={() => setShowLinkDialog(true)}>
                      Verknüpfen
                    </Button>
                  }
                />
              }
            >
              {linkedActivities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <Icon name="documents" style={{ fontSize: '2rem', color: 'var(--sapNeutralColor)', display: 'block', marginBottom: '0.5rem' }} />
                  <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
                    Noch keine Verarbeitungen verknüpft.
                  </Text>
                  <div style={{ marginTop: '0.75rem' }}>
                    <Button icon="add" design="Default" onClick={() => setShowLinkDialog(true)}>
                      Verarbeitung verknüpfen
                    </Button>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '0 1rem 1rem' }}>
                  <Table
                    style={{ width: '100%' }}
                    columns={
                      <>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Verarbeitung</Text></TableColumn>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Status</Text></TableColumn>
                        <TableColumn demandPopin minWidth={500}><Text style={{ fontWeight: '600' }}>Risiko</Text></TableColumn>
                        <TableColumn><Text style={{ fontWeight: '600' }}>Aktion</Text></TableColumn>
                      </>
                    }
                  >
                    {linkedActivities.map(activity => (
                      <TableRow key={activity.id}>
                        <TableCell>
                          <div>
                            <Text
                              style={{ fontWeight: '600', cursor: 'pointer', color: 'var(--sapLinkColor)' }}
                              onClick={() => navigate(`/processing/${activity.id}`)}
                            >
                              {activity.name}
                            </Text>
                            <Text style={{ display: 'block', fontSize: '0.8125rem', color: 'var(--sapNeutralTextColor)' }}>
                              {activity.department}
                            </Text>
                          </div>
                        </TableCell>
                        <TableCell>
                          <ObjectStatus state={processingStatusConfig[activity.status]?.state as any} showDefaultIcon>
                            {processingStatusConfig[activity.status]?.label}
                          </ObjectStatus>
                        </TableCell>
                        <TableCell>
                          {activity.risk_level ? (
                            <ObjectStatus state={riskConfig[activity.risk_level]?.state as any}>
                              {riskConfig[activity.risk_level]?.label}
                            </ObjectStatus>
                          ) : (
                            <Text style={{ color: 'var(--sapNeutralTextColor)' }}>-</Text>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            icon="disconnect"
                            design="Transparent"
                            onClick={() => handleUnlink(activity.id)}
                            tooltip="Verknüpfung entfernen"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </Table>
                </div>
              )}
            </Card>
          </div>
        </Tab>
      </TabContainer>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <MessageBox
          type="Confirm"
          open={showDeleteDialog}
          onClose={(e: any) => {
            if (e.detail.action === 'OK') handleDelete();
            setShowDeleteDialog(false);
          }}
          titleText="Maßnahme löschen"
        >
          Möchten Sie die Maßnahme „{measure.name}" wirklich löschen? Alle Verknüpfungen zu Verarbeitungen werden ebenfalls entfernt.
        </MessageBox>
      )}

      {/* Link Activity Dialog */}
      {showLinkDialog && (
        <MessageBox
          type="Confirm"
          open={showLinkDialog}
          onClose={() => setShowLinkDialog(false)}
          titleText="Verarbeitung verknüpfen"
        >
          <div style={{ maxHeight: '400px', overflow: 'auto' }}>
            {unlinkableActivities.length === 0 ? (
              <Text>Alle Verarbeitungen sind bereits verknüpft.</Text>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {unlinkableActivities.map(activity => (
                  <Button
                    key={activity.id}
                    design="Default"
                    onClick={() => handleLink(activity.id)}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    {activity.name} ({activity.department})
                  </Button>
                ))}
              </div>
            )}
          </div>
        </MessageBox>
      )}
    </div>
  );
}
